import { IDayCloseHourlyRow } from '@/interfaces/IDayClose';

export interface IHourlyBarDatum {
  /** Real clock hour (0-23) shown as the bar label. */
  hour: number;
  orders: number;
  amount: number;
  /** 0-100 relative to the busiest hour. */
  heightPct: number;
  /** True on the first bar past midnight of a wrapped axis (divider hint). */
  wrapsMidnight: boolean;
}

/**
 * Orders hourly sales rows along the business-day axis: bars start at the
 * day's cutoff hour and wrap past midnight (e.g. cutoff 8 -> 8, 9, ... 23,
 * 0, ... 7), so late-night tail sales read as the END of the trading day
 * instead of appearing before opening. Gaps between the first and last
 * active hour are zero-filled for visual continuity. A cutoff of 0 (or any
 * invalid value) preserves the plain 0-23 calendar ordering.
 */
export function buildHourlyBars(
  rows: IDayCloseHourlyRow[],
  cutoffHour: number | null | undefined,
): IHourlyBarDatum[] {
  if (!rows.length) return [];
  const cutoff =
    typeof cutoffHour === 'number' &&
    Number.isInteger(cutoffHour) &&
    cutoffHour >= 0 &&
    cutoffHour <= 23
      ? cutoffHour
      : 0;

  // Position on the business-day axis: 0 = cutoff hour, 23 = hour before it.
  const positionOf = (hour: number) => (hour - cutoff + 24) % 24;

  const byPosition = new Map<number, IDayCloseHourlyRow>(
    rows.map(row => [positionOf(row.hour), row]),
  );
  const positions = [...byPosition.keys()];
  const first = Math.min(...positions);
  const last = Math.max(...positions);
  const maxAmount = Math.max(...rows.map(row => row.amount));

  const bars: IHourlyBarDatum[] = [];
  for (let position = first; position <= last; position++) {
    const hour = (position + cutoff) % 24;
    const row = byPosition.get(position);
    const amount = row?.amount ?? 0;
    bars.push({
      hour,
      orders: row?.orders ?? 0,
      amount,
      heightPct: maxAmount > 0 ? Math.round((amount / maxAmount) * 100) : 0,
      wrapsMidnight: hour === 0 && position > first,
    });
  }
  return bars;
}
